/**
 * What AI a running session is actually using, and what the player has left. Built from resolved
 * runtime facts (auto modes settled, fallbacks taken), not from the settings, so the header chip,
 * its panel, the lesson brief and the pause menu all say the same thing.
 */

/** Why a model is not in use, or stopped being used. */
export type AiReason = 'challenge' | 'setting' | 'unavailable' | 'cap' | 'budget' | 'rate';

export interface AiAllowance {
  scope: 'account' | 'guest' | 'daily' | 'unmetered';
  secondsRemaining?: number;
  secondsLimit?: number;
  /** 0–1 of today's per-player share still unspent. */
  shareLeft?: number;
  /** The server's refusal code (`overBudget` in voice/server.mjs), or null while AI is allowed. */
  blocked?: string | null;
}

export interface SessionAiState {
  pilots: { mode: 'ai' | 'rules' | 'checking'; reason?: AiReason };
  commands: { mode: 'ai' | 'phrases' | 'checking'; reason?: AiReason };
  voices: 'off' | 'browser' | 'ai';
  mic: 'off' | 'unavailable' | 'standby' | 'connected';
  allowance: AiAllowance | null;
}

export interface AiRow { name: string; value: string; why?: string; ai: boolean }
export interface AiSummary {
  chip: { label: string; tone: 'lit' | 'warn' | 'off'; title: string };
  rows: AiRow[];
  /** One line for the lesson brief and pause menu. */
  line: string;
}

const BLOCKED: Record<string, string> = {
  account: 'Sign in to use hosted AI.',
  'trial-pool': 'The AI trial pool is used up for now.',
  allowance: 'Your AI allowance is used.',
  player: 'Your share of today\'s AI is used; it resets tomorrow.',
  day: 'Today\'s AI budget for the game is used; it resets tomorrow.',
  session: 'This session used its AI share; a new session starts fresh.',
};
const BLOCKED_SHORT: Record<string, string> = { account: 'sign in', 'trial-pool': 'trial used', allowance: 'allowance used', player: 'today\'s share used', day: 'budget used today', session: 'session share used' };

const WHY: Record<AiReason, string> = {
  challenge: 'Challenges always fly rules pilots so every run is comparable.',
  setting: 'Chosen in setup.',
  unavailable: 'No AI model is available on this server.',
  cap: 'This session used up its AI pilot allowance.',
  budget: 'The AI allowance is used.',
  rate: 'Too many requests at once; AI resumes shortly.',
};

/** What is left, in words, or null when nothing is metered for this player. */
export function allowanceText(a: AiAllowance | null): string | null {
  if (!a) return null;
  if (a.blocked) return BLOCKED[a.blocked] ?? 'AI is paused.';
  if (a.scope === 'account' && typeof a.secondsRemaining === 'number') return `${Math.floor(a.secondsRemaining / 60)} AI minutes left`;
  if (a.scope === 'daily' && typeof a.shareLeft === 'number') return `${Math.round(a.shareLeft * 100)}% of today's AI left`;
  if (a.scope === 'unmetered') return 'Not metered on this server';
  return null;
}

function low(a: AiAllowance | null): boolean {
  if (!a || a.blocked) return false;
  if (a.scope === 'account' && typeof a.secondsRemaining === 'number') return a.secondsRemaining < 300;
  return a.scope === 'daily' && typeof a.shareLeft === 'number' && a.shareLeft < 0.2;
}

export function describeSessionAi(s: SessionAiState): AiSummary {
  const blocked = s.allowance?.blocked ?? null;
  const pilotsAi = s.pilots.mode === 'ai', commandsAi = s.commands.mode === 'ai';
  const voicesAi = s.voices === 'ai', micAi = s.mic === 'standby' || s.mic === 'connected';
  const rows: AiRow[] = [
    { name: 'Pilots', ai: pilotsAi, value: s.pilots.mode === 'checking' ? 'Checking…' : pilotsAi ? 'AI crew — a model chooses the pilots\' words' : 'Rules — scripted pilot replies', why: s.pilots.reason && WHY[s.pilots.reason] },
    { name: 'Typed commands', ai: commandsAi, value: s.commands.mode === 'checking' ? 'Checking…' : commandsAi ? 'Plain English, AI interprets what standard phrases miss' : 'Standard phrases and shorthand only', why: s.commands.reason && WHY[s.commands.reason] },
    { name: 'Radio voices', ai: voicesAi, value: s.voices === 'off' ? 'Off — read the radio log' : voicesAi ? 'AI generated voices' : 'Your browser\'s built-in voices' },
    { name: 'Push to talk', ai: micAi, value: s.mic === 'off' ? 'Off' : s.mic === 'unavailable' ? 'Unavailable on this server' : s.mic === 'connected' ? 'Connected — audio goes to OpenAI only while you transmit' : 'Ready — hold Space or Alt+R', why: micAi ? 'Your microphone audio is sent to OpenAI only while you hold the key.' : undefined },
  ];
  const left = allowanceText(s.allowance);
  if (left) rows.push({ name: 'Allowance', ai: false, value: left });

  const inUse = [pilotsAi && 'pilots', commandsAi && 'commands', voicesAi && 'voices', micAi && 'mic'].filter(Boolean) as string[];
  const checking = s.pilots.mode === 'checking' || s.commands.mode === 'checking';
  let chip: AiSummary['chip'];
  if (blocked && (inUse.length || s.pilots.reason === 'budget')) chip = { label: `AI paused · ${BLOCKED_SHORT[blocked] ?? 'limit'}`, tone: 'warn', title: BLOCKED[blocked] ?? 'AI is paused.' };
  else if (!inUse.length) chip = { label: checking ? 'AI …' : 'No AI', tone: 'off', title: checking ? 'Checking which AI services are available.' : 'Rules pilots and standard phrases; nothing is sent to a model.' };
  else {
    const short = s.allowance?.scope === 'account' && typeof s.allowance.secondsRemaining === 'number' ? `${Math.floor(s.allowance.secondsRemaining / 60)} min left`
      : s.allowance?.scope === 'daily' && typeof s.allowance.shareLeft === 'number' ? `${Math.round(s.allowance.shareLeft * 100)}% left today` : '';
    chip = { label: `AI on${short ? ` · ${short}` : ''}`, tone: low(s.allowance) ? 'warn' : 'lit', title: `AI in use: ${inUse.join(', ')}.${left ? ` ${left}.` : ''}` };
  }
  const line = [
    `Pilots: ${pilotsAi ? 'AI crew' : s.pilots.mode === 'checking' ? 'checking' : 'rules'}`,
    `Commands: ${commandsAi ? 'AI assisted' : s.commands.mode === 'checking' ? 'checking' : 'standard phrases'}`,
    `Voices: ${s.voices === 'off' ? 'off' : voicesAi ? 'AI' : 'browser'}`,
    s.mic !== 'off' ? `Mic: ${s.mic === 'unavailable' ? 'unavailable' : 'on'}` : '',
    left ?? '',
  ].filter(Boolean).join(' · ');
  return { chip, rows, line };
}
